import axios, { AxiosError } from "axios";
import { API_URL, AUTH_CUSTOMERS_BASE, PLATFORM_DEALER_ID } from "@/config";

export const customerHttp = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

function pickErrorMessage(err: unknown): string {
    if (axios.isAxiosError(err)) {
        const ax = err as AxiosError<unknown>;
        const data = ax.response?.data;
        if (typeof data === "string") return data;
        if (data && typeof data === "object") {
            if ("message" in data && typeof data.message === "string") return data.message;
            if ("error" in data && typeof data.error === "string") return data.error;
        }
        return ax.message || "Request failed";
    }
    return err instanceof Error ? err.message : "Request failed";
}

export interface CustomerAuthUser {
    customer_id: number;
    customer_dealer_id: number;
    customer_email: string | null;
    customer_fname: string | null;
    customer_lname: string | null;
    customer_created_at: string;
    email_verified_at: string | null;
    last_login_at: string | null;
}

export async function loginCustomer(email: string, password: string): Promise<{ ok: boolean; customer?: CustomerAuthUser; message?: string }> {
    try {
        const res = await fetch(`${AUTH_CUSTOMERS_BASE}/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email, password, dealerId: PLATFORM_DEALER_ID }),
            credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || data?.error || "Login failed");
        return data;
    } catch (e) {
        throw new Error(pickErrorMessage(e));
    }
}

export async function registerCustomer(payload: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phone?: string;
}): Promise<CustomerAuthUser> {
    try {
        const res = await fetch(`${AUTH_CUSTOMERS_BASE}/register`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...payload, dealerId: PLATFORM_DEALER_ID }),
            credentials: "include",
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || data?.error || "Registration failed");
        return data;
    } catch (e) {
        throw new Error(pickErrorMessage(e));
    }
}

export async function getCurrentCustomer(): Promise<CustomerAuthUser | null> {
    try {
        const res = await customerHttp.get<{ ok: boolean; customer?: CustomerAuthUser }>("/auth/me", {
            withCredentials: true,
            validateStatus: (s) => (s >= 200 && s < 300) || s === 401,
        });
        return res.status === 401 ? null : res.data.customer ?? null;
    } catch {
        return null;
    }
}

export async function logoutCustomer(): Promise<void> {
    try {
        await customerHttp.post("/auth/logout");
    } catch (e) {
        throw new Error(pickErrorMessage(e));
    }
}
